import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import {
  Text,
  Spinner,
  Button,
} from '@cochlear-design-system/foundation';
import { Error } from '../Error/Error';
import AutoReloadIFrame from '../../components/AutoReloadIFrame';
import { ScheduleSRPortal } from '../../components/ScheduleSRPortal';
import {
  ACCESS_TOKEN_COOKIE,
  ID_TOKEN_COOKIE,
  envUrl,
  envUrlSRPortal,
  routes,
} from '../../config';
import cookieService from '../../services/cookieService';
import providerService from '../../services/providerService';

const srPortalConfig = {
  enabled: true,
  scheduleSRPortalScriptPath: `${envUrlSRPortal}/static/chunks/remoteEntry.js`,
  apiBaseUrl: envUrlSRPortal,
};

export const ServiceRequest = ({
  config,
  country,
  labels,
  lang,
  useModuleFederationSR,
  providerRegion: _providerRegion, // eslint-disable-line no-unused-vars
  patientId,
}) => {
  const history = useHistory();
  const location = useLocation();
  const accessToken = cookieService.getCookie(ACCESS_TOKEN_COOKIE);
  const idToken = cookieService.getCookie(ID_TOKEN_COOKIE);
  const { providerId } = providerService.getProviderDetails();
  const logoutUrl = `${envUrl}${routes.dpx.logout}`;

  const getViewServiceRequestPath = () => {
    if (country && lang) {
      return `/${country}/${lang}/${routes.dpx.viewServiceRequest}`;
    }

    return location.pathname.replace(
      routes.dpx.patientDetails,
      routes.dpx.viewServiceRequest,
    );
  };

  const handleSRClick = (srNo, _patientIdArg, csrId) => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.set('tab', 'service-requests');
    const search = searchParams.toString();

    history.push({
      pathname: getViewServiceRequestPath(),
      state: {
        serviceRequestId: srNo,
        patientId,
        csrId,
        returnPath: `${location.pathname}${
          search ? `?${search}` : ''
        }`,
        source: 'service-request-history',
      },
    });
  };

  // For CAM/APAC regions, render Module Federation component via wrapper
  if (useModuleFederationSR) {
    return (
      <ScheduleSRPortal
        config={srPortalConfig}
        onSRClick={handleSRClick}
        accessToken={accessToken}
        idToken={idToken}
        selectedProviderId={providerId}
        pageType="ServiceRequestHistory"
        logoutUrl={logoutUrl}
        patientId={patientId}
        hideInternalNavigation
      />
    );
  }

  // For EMEA/LATAM regions, use existing MCP iframe approach
  // View
  let showView = '';
  if (config.serviceRequestConfig.hasError) {
    showView = 'error';
  } else if (config.isLoading !== false) {
    showView = 'spinner';
  } else if (config.isEmpty !== false) {
    showView = 'add_device';
  } else {
    showView = 'sr_frame';
  }

  const disableIframes =
    localStorage.getItem('disableIframes') === 'true';

  let serviceRequestView = null;
  switch (showView) {
    case 'error': {
      serviceRequestView = (
        <div>
          <Error
            config={config.serviceRequestConfig.errorConfig}
            labels={labels}
            data={{}}
          />
        </div>
      );
      break;
    }

    case 'add_device': {
      serviceRequestView = (
        <div className="ccl__align-cm5">
          <Text
            content={
              labels[config.labels.serviceRequestsNotApplicable]
            }
          />
          <br />
          <Button
            text={labels[config.labels.addDevice]}
            variant="brand-primary"
            icon="add"
            iconPosition="leading"
            size="medium"
            onClick={config.serviceRequestConfig.button?.onClick}
            data-analytics={
              config.serviceRequestConfig.button?.analytics
            }
          />
        </div>
      );
      break;
    }

    case 'sr_frame': {
      let iframeStyle = { display: 'none' };
      if (config.serviceRequestConfig.isLoading === false) {
        iframeStyle = { width: '100%', height: '100vh' };
      }

      serviceRequestView = (
        <>
          {config.serviceRequestConfig.isLoading && (
            <div className="ccl__spinner">
              <Spinner animation="border" />
              <Text content={labels[config.labels.loading]} />
            </div>
          )}
          <div>
            {!disableIframes && (
              <AutoReloadIFrame
                src={config.serviceRequestConfig.serviceRequestUrl}
                title="Service Request"
                style={iframeStyle}
                enabled={config.serviceRequestConfig.isLoading}
                timeSeconds={10}
              />
            )}
          </div>
        </>
      );
      break;
    }

    default: {
      serviceRequestView = (
        <div className="ccl__spinner">
          <Spinner animation="border" />
          <Text content={labels[config.labels.loading]} />
        </div>
      );
    }
  }

  return <>{serviceRequestView}</>;
};
